import React, { useContext, useState } from "react";
import { Box, Button, Container, Grid } from "@mui/material";
import Layout from "components/layout/layout";
import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as DownloadIcon } from "./../../../assets/Icons/download.svg";
import { ReactComponent as ExtendedIcon } from "./../../../assets/Icons/extended.svg";
import { ReactComponent as GrantIcon } from "./../../../assets/Icons/Grant.svg";
import { ReactComponent as EditIcon } from "./../../../assets/Icons/edit.svg";

import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { TransactionDetailsCard } from "components/transaction-details/transaction-details";
import { TransactionDetailsItem } from "components/transaction-details/transaction-details-item";
import { shortFormat, shortFormat2 } from "utils/date-format";
import { useGetGrantRequestQuery } from "hooks/use-services/grant-request.service";
import {
  GRANT_REQUEST_STATUS_ENUM,
  GRANT_REQUEST_TYPE_ENUM,
  OnTimeSchedule,
  RecurrentSchedule,
  getExecutionDate,
} from "types/grant-request";
import { primary } from "style/color";

import { MyGrantsRequestsPagePath } from "constants/pages";
import { CancelGrant } from "./cancel-grant";
import { BackToPreviousButton } from "components/shared/back-to-previous-button";
import { einFormat } from "types/charity";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { USER_ROLE } from "types/account";

export const MainGrantTransactionDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: grantRequest, isSuccess } = useGetGrantRequestQuery(
    id ? +id : 0
  );
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
  const { userRole } = useContext<AuthData>(AuthContext);

  const getStatusCard = () => {
    if (grantRequest?.status === GRANT_REQUEST_STATUS_ENUM.PENDING) {
      return {
        icon: <ExtendedIcon />,
        title: "Pending",
        text: "Grants appear as pending until the payment is received. .",
      };
    }
    if (grantRequest?.status === GRANT_REQUEST_STATUS_ENUM.REJECTED) {
      return {
        icon: <HighlightOffIcon sx={{ color: primary[500] }} />,
        title: "Rejected",
        text: `Our team didn’t approve this transaction due to ${grantRequest.rejectionReason}`,
      };
    }
    if (
      grantRequest?.status === GRANT_REQUEST_STATUS_ENUM.SCHEDULED &&
      grantRequest.type === GRANT_REQUEST_TYPE_ENUM.ONETIME
    ) {
      return {
        icon: <ExtendedIcon />,
        title: `Scheduled for ${shortFormat2(
          (grantRequest.scheduleInformation as OnTimeSchedule).executionDate
        )}`,
        text: `Grants appear as pending until the payment is received. `,
      };
    }
    if (
      grantRequest?.status === GRANT_REQUEST_STATUS_ENUM.SCHEDULED &&
      grantRequest.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT
    ) {
      const frequency = (grantRequest?.scheduleInformation as RecurrentSchedule)
        .frequency;

      return {
        icon: <ExtendedIcon />,
        title: `${frequency.charAt(0) + frequency.substr(1).toLowerCase()
          } recurrence`,
        text: `The next Grant will be scheduled on ${shortFormat(
          (grantRequest.scheduleInformation as RecurrentSchedule)
            .nextExecutionDate
        )} `,
      };
    }
    return undefined;
  };
  const handleCloseCancelDialog = (redirect: boolean) => {
    setOpenCancelDialog(false);
    if (redirect) {
      navigate(MyGrantsRequestsPagePath);
    }
  };
  return (
    <Layout>
      {() => (
        <>
          <Box sx={{ my: 4, width: "100%" }}>
            <React.Fragment>
              <Container>
                {isSuccess && grantRequest && (
                  <>
                    <CancelGrant
                      open={openCancelDialog}
                      onClose={handleCloseCancelDialog}
                      grantRequest={grantRequest!}
                    />
                    <Grid sx={{ m: 5 }} container spacing={2}>
                      <Grid item xs={12} sm={9}>
                        <BackToPreviousButton />
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TransactionDetailsCard
                          amount={grantRequest.information.appliedAmount!}
                          type={"Grant"}
                          title={grantRequest.charity.name}
                          ein={(grantRequest.charity.country === "USA") ? einFormat(grantRequest.charity.ein) : grantRequest.charity.ein}
                          avatar={<GrantIcon />}
                          status={grantRequest.status}
                          statusCard={getStatusCard()}
                        >
                          <TransactionDetailsItem
                            label="Type"
                            value={
                              grantRequest.type ===
                                GRANT_REQUEST_TYPE_ENUM.RECURRENT
                                ? "Recurring Grant"
                                : "Grant"
                            }
                          />
                          {grantRequest.type ===
                            GRANT_REQUEST_TYPE_ENUM.RECURRENT && (
                              <TransactionDetailsItem
                                label="recurrency"
                                value={
                                  (
                                    grantRequest?.scheduleInformation as RecurrentSchedule
                                  ).frequency.charAt(0) +
                                  (
                                    grantRequest?.scheduleInformation as RecurrentSchedule
                                  ).frequency
                                    .substr(1)
                                    .toLowerCase()
                                }
                              />
                            )}
                          <TransactionDetailsItem
                            label="Date"
                            value={shortFormat(getExecutionDate(grantRequest))!}
                          />
                          <TransactionDetailsItem
                            label="Initiated by"
                            value={`${grantRequest.user.firstName} ${grantRequest.user.lastName}`}
                          />
                          <TransactionDetailsItem
                            label="Fund"
                            value={grantRequest.fundAccount.fundAccountName}
                          />
                        </TransactionDetailsCard>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        {grantRequest.status ===
                          GRANT_REQUEST_STATUS_ENUM.PROCESSED && (
                            <Button
                              fullWidth
                              startIcon={<DownloadIcon />}
                              variant="contained"
                            >
                              Download
                            </Button>
                          )}
                        {(grantRequest.status ===
                          GRANT_REQUEST_STATUS_ENUM.SCHEDULED ||
                          grantRequest.status ===
                          GRANT_REQUEST_STATUS_ENUM.PENDING) && (
                            <>
                              <Button
                                disabled={userRole === USER_ROLE.VIEWER}
                                fullWidth
                                startIcon={<EditIcon />}
                                variant="contained"
                                onClick={() => {
                                  navigate({
                                    pathname: `/my-grants/edit/${grantRequest.grantRequestId}`,
                                  });
                                }}
                              >
                                Edit
                              </Button>
                              <br></br>
                              <Button
                                disabled={userRole === USER_ROLE.VIEWER}
                                sx={{ mt: 1 }}
                                fullWidth
                                size="large"
                                startIcon={<CloseIcon />}
                                variant="contained"
                                onClick={() => {
                                  setOpenCancelDialog(true);
                                }}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Container>
            </React.Fragment>
          </Box>
        </>
      )}
    </Layout>
  );
};
